<template>
  <div class="filtro-mes">
    <div class="year-selector mb-3">
      <div class="d-flex align-items-center justify-content-center gap-3">
        <i
          class="fa-solid fa-chevron-left pointer"
          :class="{ disabled: !canBack }"
          @click="canBack && changeYear(-1)" />
        <span class="p medium text-landing">{{ year }}</span>
        <i
          class="fa-solid fa-chevron-right pointer"
          :class="{ disabled: !canNext }"
          @click="canNext && changeYear(1)" />
      </div>
    </div>

    <ul class="p-2">
      <li
        v-for="(month, index) in months"
        :key="index"
        class="dropdown-item padding-x-5 user-select-none d-flex justify-content-between align-items-center text-landing pointer"
        :class="{ 'month-selected': isSelected(index) }"
        @click="selectMonth(index)">
        {{ month }}
      </li>
    </ul>
  </div>
</template>

<script setup>
  import { ref, computed, onBeforeMount } from 'vue';
  import moment from 'moment-timezone';

  const props = defineProps({
    configuracionFiltro: {
      type: Object,
      required: true,
    },
  });

  const emit = defineEmits(['selected']);

  const year = ref(new Date().getFullYear());

  const canBack = computed(() => {
    if (!props.configuracionFiltro.yearsDisponibles?.length) return false;
    const currentIndex = props.configuracionFiltro.yearsDisponibles.indexOf(year.value);
    return currentIndex > 0;
  });

  const canNext = computed(() => {
    if (!props.configuracionFiltro.yearsDisponibles?.length) return false;
    const currentIndex = props.configuracionFiltro.yearsDisponibles.indexOf(year.value);
    return currentIndex < props.configuracionFiltro.yearsDisponibles.length - 1;
  });

  const months = computed(() => {
    if (!year.value) return [];

    const meses = [];
    for (let i = 0; i < 12; i++) {
      const fecha = moment({ year: year.value, month: i });
      if (fecha) {
        meses.push(fecha.format('MMMM').charAt(0).toUpperCase() + fecha.format('MMMM').slice(1));
      }
    }
    return meses;
  });

  function isSelected(monthIndex) {
    if (!props.configuracionFiltro.idsAplicados) return false;
    const monthId = `mes-${year.value}-${monthIndex}`;
    return props.configuracionFiltro.idsAplicados.has(monthId);
  }

  function selectMonth(monthIndex) {
    const fecha = moment({ year: year.value, month: monthIndex });
    if (!fecha) {
      return;
    }

    const nombreMes = fecha.format('MMMM YYYY').charAt(0).toUpperCase() + fecha.format('MMMM YYYY').slice(1);

    emit('selected', {
      id: `mes-${year.value}-${monthIndex}`,
      idFiltro: props.configuracionFiltro.id,
      tituloFiltro: props.configuracionFiltro.titulo,
      valor: {
        year: year.value,
        month: monthIndex,
        nombre: nombreMes,
      },
    });
  }

  function changeYear(delta) {
    if (!props.configuracionFiltro.yearsDisponibles?.length) return;

    const currentIndex = props.configuracionFiltro.yearsDisponibles.indexOf(year.value);
    const newIndex = currentIndex + delta;

    if (newIndex >= 0 && newIndex < props.configuracionFiltro.yearsDisponibles.length) {
      year.value = props.configuracionFiltro.yearsDisponibles[newIndex];
    }
  }

  onBeforeMount(() => {
    if (props.configuracionFiltro?.yearsDisponibles?.length > 0) {
      year.value = props.configuracionFiltro.yearsDisponibles[0];
    } else {
      year.value = new Date().getFullYear();
    }
  });
</script>

<style scoped lang="scss">
  .filtro-mes {
    min-width: 200px;
    width: max-content;

    @media screen and (max-width: 768px) {
      width: 100%;
    }
  }

  .year-selector {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 10px 0 10px;

    i {
      color: rgb(var(--landing-text-color-rgb, var(--bs-primary-rgb)));
      font-size: 16px;
      transition: opacity 0.2s ease;

      &:hover:not(.disabled) {
        opacity: 0.7;
      }

      &.disabled {
        opacity: 0.3;
        cursor: not-allowed;
        pointer-events: none;
      }
    }
  }

  .dropdown-item {
    &:active {
      background-color: rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.1);
    }

    &.month-selected {
      color: rgb(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.5) !important;
      pointer-events: none;

      &:hover,
      &:active {
        background-color: var(--bs-secondary);
        color: var(--bs-white) !important;
      }
    }

    @media screen and (max-width: 768px) {
      margin-bottom: 10px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .padding-x-5 {
    padding-left: 5px !important;
    padding-right: 5px !important;
  }
</style>
